/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { breakpointsMap, SYS_SPACE_FIXED_250 } from '@oneaudi/unified-web-common';
import { Layout, LayoutItem, TextLink } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import { Image } from './Image';
import { messages } from '../../../i18n/messages';

import { useTrackingManager } from '../../../hooks/useTrackingManager';
import { useMedia } from '../../../hooks/useMedia';
import { CtaBar } from './CtaBar';
import { ctaBarButtonConfigProps } from './pbc-types';

import { PartnerContext } from '../../../contexts/partner-context';
import { MyAudiCtaBar } from './MyAudiCtaBar';
import { MyAudiBarButtonConfig } from '../../../types';

const StyledImage = styled.figure`
  position: relative;
  margin: 0;
  /* create aspect ratio box with an aspect ratio of 16:9 */
  padding: 0 0 56.25% 0;
  overflow: hidden;

  > img {
    display: block;
    position: absolute;
    width: 100%;
    height: auto;
  }
`;

const ColumnItemPicture = styled(LayoutItem)`
  width: 70%;
  margin-inline-end: var(${(props: any) => props.theme.responsive.spacing.xl});

  @media (max-width: 1023px) {
    margin-inline-end: 0;
    max-width: 100%;
  }
`;

const ColumnItemInformation = styled(LayoutItem)`
  align-self: auto;
  width: 100%;
`;

const ButtonAlignedLeft = styled(TextLink)`
  text-align: left;
  min-height: auto;
  text-decoration: none !important;
`;

interface AdressAndContactProps {
  displayCtaBar: boolean;
  ctaBarButtonConfig: ctaBarButtonConfigProps[];
  isNarrow?: boolean;
  isLinkedToPWS?: boolean;
  myAudiCtaConfig?: MyAudiBarButtonConfig;
  errorMessage?:
    | {
        code: string;
        message: string;
      }
    | undefined;
}

export const AddressAndContact = ({
  displayCtaBar,
  isNarrow = undefined,
  ctaBarButtonConfig,
  isLinkedToPWS = true,
  myAudiCtaConfig,
  errorMessage,
}: AdressAndContactProps): React.ReactElement => {
  const trackingManager = useTrackingManager();
  const { partner } = React.useContext(PartnerContext);
  const vCardLinkLabel = useI18n(messages.vCardLinkLabel);
  const vCardMoreLinkLabel = useI18n(messages.linkLabel);
  const [partnerUrl, setPartnerUrl] = React.useState(partner?.url);

  React.useEffect(() => {
    setPartnerUrl(partner?.url);
    // @ts-expect-error - check if mcvid exists
    const visitor = window?.ss?.visitor;
    const rawPartnerUrl = partner?.url;
    if (rawPartnerUrl && visitor) {
      const mcvid = visitor?.getMarketingCloudVisitorID();
      const newPartnerUrl = rawPartnerUrl.includes('?')
        ? rawPartnerUrl.concat(`&mcvid=${mcvid}`)
        : rawPartnerUrl.concat(`?mcvid=${mcvid}`);
      setPartnerUrl(newPartnerUrl);
    }
  }, [partner]);

  const isViewportM = useMedia(`only screen and (min-width: ${breakpointsMap.md})`);

  // TODO Check imgWidth value
  const imgWidth = 328;
  // const stackEndMargin = isNarrow ? 'l' : 'xl';
  const partnerName = partner.nameEnhanced;

  const partnerImage = (
    <StyledImage>
      <Image
        loadingAnimation="fade"
        src={`${partner.locationImageUrl}?width=${imgWidth}`}
        data-testid="image"
        alt={partner.displayName}
        sizes={[
          {
            condition: `(max-width: ${imgWidth}px)`,
            value: `${imgWidth}px`,
          },
          {
            condition: '(max-width: 639px)',
            value: '656px',
          },
          {
            condition: '(max-width: 1023px)',
            value: `${imgWidth}px`,
          },
          {
            value: '656px',
          },
        ]}
        srcSet={[
          {
            src: `${partner.locationImageUrl}?width=${imgWidth}`,
            desc: `${imgWidth}w`,
          },
          {
            src: `${partner.locationImageUrl}?width=656`,
            desc: '656w',
          },
        ]}
      />
    </StyledImage>
  );

  return (
    <>
      {partner && (
        <Layout direction={{ xs: 'column', lg: 'row' }}>
          {partner.locationImageUrl && isViewportM && !isNarrow && (
            <ColumnItemPicture margin={[0, 0, 'xxl', 0]} shrink="0" grow="0" basis="50%">
              {isLinkedToPWS && partnerUrl ? (
                <a
                  onClick={() =>
                    trackingManager.click(
                      'exit_link',
                      partnerUrl,
                      'image',
                      '',
                      partnerName,
                      '',
                      'pws link',
                    )
                  }
                  href={partnerUrl}
                >
                  {partnerImage}
                </a>
              ) : (
                <>{partnerImage}</>
              )}
            </ColumnItemPicture>
          )}

          <ColumnItemInformation>
            <Layout direction="column">
              <ButtonAlignedLeft
                variant="standalone"
                size="md"
                icon="geofence"
                iconPosition="leading"
                href={partner.directionsUrl}
                onClick={() =>
                  trackingManager.click(
                    'exit_link',
                    partner.directionsUrl,
                    'link',
                    `${partner.address[0]} ${partner.address[1]}`,
                    partnerName,
                    '1',
                    'location',
                  )
                }
                data-testid="directions"
                margin={[0, 0, SYS_SPACE_FIXED_250]}
              >
                {partner.address[0]}
                <br />
                {partner.address[1]}
              </ButtonAlignedLeft>

              <ButtonAlignedLeft
                href={`tel:${partner.phone}`}
                data-testid="phone"
                variant="standalone"
                size="md"
                icon="voice-call"
                iconPosition="leading"
                onClick={() =>
                  trackingManager.click(
                    'call',
                    partner.directionsUrl,
                    'link',
                    'Anrufen',
                    partnerName,
                    '2',
                    'phone',
                  )
                }
                margin={[0, 0, SYS_SPACE_FIXED_250]}
              >
                {partner.phone}
              </ButtonAlignedLeft>

              {partner.fax && (
                <ButtonAlignedLeft
                  href={`tel:${partner.fax}`}
                  data-testid="fax"
                  variant="standalone"
                  size="md"
                  onClick={() =>
                    trackingManager.click(
                      'call',
                      partner.directionsUrl,
                      'link',
                      'Anrufen',
                      partnerName,
                      '2',
                      'fax',
                    )
                  }
                  margin={[0, 0, SYS_SPACE_FIXED_250]}
                >
                  {useI18n(messages.fax)}: {partner.fax}
                </ButtonAlignedLeft>
              )}

              {partner.email && (
                <ButtonAlignedLeft
                  href={`mailto:${partner.email}`}
                  variant="standalone"
                  size="md"
                  icon="direct-mail"
                  iconPosition="leading"
                  onClick={() =>
                    trackingManager.click(
                      'email',
                      partner.directionsUrl,
                      'link',
                      partner.email as string,
                      partnerName,
                      '3',
                      'mail',
                    )
                  }
                  margin={[0, 0, SYS_SPACE_FIXED_250]}
                >
                  {partner.email}
                </ButtonAlignedLeft>
              )}

              <ButtonAlignedLeft
                variant="standalone"
                size="md"
                href={partner.vCardUrl}
                onClick={() =>
                  trackingManager.click(
                    'download',
                    partner.vCardUrl,
                    'link',
                    vCardLinkLabel,
                    partnerName,
                    '4',
                    'contact data download',
                  )
                }
                data-testid="vcard"
                icon="address-book"
                iconPosition="leading"
                margin={[0, 0, SYS_SPACE_FIXED_250]}
              >
                {useI18n(messages.vCardLinkLabel)}
              </ButtonAlignedLeft>

              {isLinkedToPWS && partnerUrl && (
                <ButtonAlignedLeft
                  variant="standalone"
                  size="md"
                  href={partnerUrl}
                  // newWindow={isNarrow}
                  target={isNarrow ? '_blank' : '_self'}
                  onClick={() =>
                    trackingManager.click(
                      'exit_link',
                      partnerUrl,
                      'link',
                      vCardMoreLinkLabel,
                      partnerName,
                      '5',
                      'pws link',
                    )
                  }
                  data-testid="vcard"
                  icon="external-link"
                  iconPosition="leading"
                >
                  {useI18n(messages.linkLabel)}
                </ButtonAlignedLeft>
              )}
            </Layout>
            {displayCtaBar && ctaBarButtonConfig && (
              <CtaBar
                ctaBarButtonConfig={ctaBarButtonConfig}
                displayname={partnerName}
                partnerId={partner.partnerId}
                favoritePartner={myAudiCtaConfig?.favoritePartner}
              />
            )}
            {myAudiCtaConfig && !displayCtaBar && (
              <MyAudiCtaBar
                myAudiCtaButtonConfig={myAudiCtaConfig}
                errorMessage={errorMessage}
                displayname={partnerName}
              />
            )}
          </ColumnItemInformation>
        </Layout>
      )}
    </>
  );
};
